import { useEffect } from "react";

import type { MeetingModelJson, VideoConferencingProviderEnum } from "@performyard/service-contracts";
import type { GetUserCalendarIntegrationStatusResponse } from "@performyard/service-contracts";

import {
	useMeetingsIntegrationsAvailableProviders,
	useMeetingsIntegrationsSettings,
	useUserCalendarIntegrationStatus,
	useUserVideoConferencingStatus,
	useVideoConferencingFeatureEnabled,
} from "../../../api/hooks/defs/meetings-integrations";
import { useFeatureFlagQuery } from "../../../hooks/useFeatureFlag";

// Lives in the api layer so the provider-display-name hook can self-gate on it without a
// circular import; re-exported here for the meetings UI's existing callers.
export { useVideoConferencingFeatureEnabled };

/**
 * Custom hook to manage main navigation active state for meeting workspace details table view.
 * Dynamically sets the correct navigation item based on admin status and participation.
 */
export const useWorkspaceNavigationState = (params: { isAdmin: boolean; isParticipant: boolean }): void => {
	const { isAdmin, isParticipant } = params;

	useEffect(() => {
		const updateMainNavigation = (): void => {
			const allNavItems = document.querySelectorAll("#header .links .top-item");
			allNavItems.forEach((item) => item.classList.remove("active"));

			if (isAdmin && !isParticipant) {
				const companyNavItem = document.querySelector('#header .links a[title="Company Info"]');
				companyNavItem?.classList.add("active");
			} else {
				const meetingWorkspacesNavItem = document.querySelector(
					'#header .links a[href="/meeting-workspaces/"]'
				);
				meetingWorkspacesNavItem?.classList.add("active");
			}
		};

		updateMainNavigation();
	}, [isAdmin, isParticipant]);
};

const SUPPORTED_CALENDAR_PROVIDERS = ["google", "outlook"] as const;

type CalendarIntegrationStatus = {
	isCalendarIntegrationEnabled: boolean;
	userCalendarStatus: GetUserCalendarIntegrationStatusResponse | undefined;
};

/**
 * Reads the eligibility gates for showing video-conferencing affordances:
 *   1) The video conferencing feature is flag-enabled for the company
 *      (`useVideoConferencingFeatureEnabled` — the single source for that gate),
 *   2) The user has connected at least one available provider,
 *   3) The company admin has enabled video conferencing.
 *
 * Mirrors the backend preHandler gate.
 *
 * Returns `eligible: false` while any underlying query is still loading so the UI
 * never flashes affordances before the gates are resolved.
 */
export const useVideoConferencingEligibility = (): {
	eligible: boolean;
	/** Whether the company admin has enabled video conferencing. Gates the Add/Remove VC affordances. */
	adminEnabled: boolean;
	isLoading: boolean;
	/**
	 * Every provider the user currently has connected — a user can connect many; a meeting uses
	 * exactly one. The add-VC affordances render a checkbox for a single entry and a select when
	 * there are several (labels resolve statically from the provider config). Irrelevant once a
	 * meeting already has VC — removal reads the provider off the stored meeting.
	 */
	connectedProviders: { provider: VideoConferencingProviderEnum }[];
} => {
	const ffEnabled = useVideoConferencingFeatureEnabled();
	const { data: settings, isLoading: isSettingsLoading } = useMeetingsIntegrationsSettings({
		enabled: ffEnabled,
	});
	const adminEnabled = !!settings?.video_conferencing?.enabled;
	// The status/providers endpoints are company-setting-gated (they 403 when VC is off), so only fire
	// them once the company has VC enabled — mirrors useCalendarIntegrationStatus.
	const { data: userStatus, isLoading: isUserStatusLoading } = useUserVideoConferencingStatus({
		enabled: ffEnabled && adminEnabled,
	});
	const { data: providersData, isLoading: isProvidersLoading } = useMeetingsIntegrationsAvailableProviders({
		enabled: ffEnabled && adminEnabled,
	});

	const isLoading = isUserStatusLoading || isProvidersLoading || isSettingsLoading;
	// Intersect the user's connections with the server-driven provider list.
	const connectedProviderNames = new Set((userStatus?.connections ?? []).map((connection) => connection.provider));
	const connectedProviders = (providersData?.providers ?? [])
		.filter(({ provider }) => connectedProviderNames.has(provider))
		.map(({ provider }) => ({ provider }));
	const eligible = ffEnabled && connectedProviders.length > 0 && adminEnabled;

	return { eligible, adminEnabled, isLoading, connectedProviders };
};

/**
 * Whether the calendar feature is flag-enabled for the company. Gates the calendar icon in the
 * meetings table (independent of whether a calendar is connected). Currently its own flag; will
 * migrate to `ff-is-meetings-integrations-enabled` alongside video conferencing.
 */
export const useCalendarFeatureEnabled = (): boolean => {
	const { value: ffIsCalendarIntegrationEnabled } = useFeatureFlagQuery("ff-is-calendar-integration-enabled", false);
	return !!ffIsCalendarIntegrationEnabled;
};

/**
 * Custom hook to check if calendar integration is enabled for the current user.
 * Consolidates the calendar integration check logic for reuse across components.
 */
export const useCalendarIntegrationStatus = (): CalendarIntegrationStatus => {
	const { value: ffIsCalendarIntegrationEnabled } = useFeatureFlagQuery("ff-is-calendar-integration-enabled", false);
	const { data: meetingsIntegrationsSettings } = useMeetingsIntegrationsSettings({
		enabled: ffIsCalendarIntegrationEnabled,
	});
	const calendarSettingEnabled = meetingsIntegrationsSettings?.calendar?.enabled === true;
	const { data: userCalendarStatus } = useUserCalendarIntegrationStatus({
		enabled: ffIsCalendarIntegrationEnabled && calendarSettingEnabled,
	});

	const isCalendarIntegrationEnabled = Boolean(
		ffIsCalendarIntegrationEnabled &&
		calendarSettingEnabled &&
		userCalendarStatus?.isConnected &&
		SUPPORTED_CALENDAR_PROVIDERS.includes(
			userCalendarStatus?.provider as (typeof SUPPORTED_CALENDAR_PROVIDERS)[number]
		) &&
		userCalendarStatus?.selectedCalendarId
	);

	return { isCalendarIntegrationEnabled, userCalendarStatus };
};

type MeetingCalendarIntegrationState = {
	isCalendarIntegrationEnabled: boolean;
	hasCalendarEvent: boolean;
	isCalendarMismatch: boolean;
};

/**
 * Custom hook to determine calendar integration state for a meeting.
 * Extracts complex calendar logic from components for better testability and reuse.
 */
export const useMeetingCalendarIntegration = (meeting: MeetingModelJson): MeetingCalendarIntegrationState => {
	const { isCalendarIntegrationEnabled, userCalendarStatus } = useCalendarIntegrationStatus();

	const hasCalendarEvent = Boolean(meeting.calendarEvent?.eventId);

	const isCalendarMismatch = Boolean(
		meeting.calendarEvent?.eventId &&
		meeting.calendarEvent?.provider &&
		userCalendarStatus &&
		!(
			userCalendarStatus.provider === meeting.calendarEvent.provider &&
			userCalendarStatus.selectedCalendarId === meeting.calendarEvent.calendarId
		)
	);

	return { isCalendarIntegrationEnabled, hasCalendarEvent, isCalendarMismatch };
};
